import userApi from '@/api/services/user'
import businessApi from '@/api/services/business'
import Util from '@/util'

const state = {
  enforceSignin: false,
  user: null,
  verifyToken: null,
  sessionToken: null,
  lastSearchedAddr: null,
  recentOutletVOs: [],
  recentOrders: [],
  currentLocation: null
};

const getters = {
  user(state) {
    return state.user;
  },

  recentOutlets: (state) => {
    if (!state.user) return null

    const recentOutlets = state.user.visitedOutlets
    if (!recentOutlets) {
      return []
    }
    return Util.pluck(recentOutlets, 'outletId')
  }
};

const actions = {
  isSignedIn({ commit, state }, callback) {
    if (!state.user) {
      return commit('enforceSignin', true);
    }
    return callback();
  },

  closeSignin({ commit }) {
    commit('enforceSignin', false);
    commit('updateVerifyToken', null);
  },

  async signIn({ commit }, params) {
    const rParams = Object.assign({ ISDCode: '+91' }, params);
    const token = await userApi.signIn(rParams) || {};
    commit('updateVerifyToken', token);
  },

  async verifyOTP({ commit, state, dispatch }, params) {
    const rParams = Object.assign({ token: state.verifyToken }, params);
    const result = await userApi.verifyOTP(rParams);
    if (result.user.firstName) {
      commit('enforceSignin', false);
    }
    commit('updateUser', result.user);
    commit('updateSessionToken', result.token);
    commit('updateVerifyToken', null);
    dispatch('updateCustomerInfo', result.user)
  },

  async signOut({ commit, state }, params = {}) {
    if (state.sessionToken) {
      await userApi.signOut({ token: state.sessionToken });
    }

    commit('updateUser', null);
    commit('updateSessionToken', null);

    if (params.enforceSignin) {
      commit('enforceSignin', true);
    }
  },

  async get({ commit, state, dispatch }) {
    if (state.user) {
      try {
        const user = await userApi.get(state.user.userId);
        commit('updateUser', user);
        dispatch('updateCustomerInfo', user)
      } catch (e) { }
    }
  },

  async update({ commit, state, dispatch }, params) {
    const rParams = Object.assign({}, state.user, params);
    try {
      const updatedUser = await userApi.update(rParams);
      commit('updateUser', updatedUser);
      if (updatedUser.firstName) {
        commit('enforceSignin', false);
        dispatch('updateCustomerInfo', updatedUser)
      }
    } catch (error) {
      if (error.code === 'ERR_VERSION_MISMATCH') {
        commit('updateUser', error.user);
      }
      throw error;
    }
  },

  async updateVisitDate({ dispatch, state }, visitedOutlet) {
    if (!state.user) return;
    const visitedOutlets = state.user.visitedOutlets || [];
    let found = false;
    for (let i = 0; i < visitedOutlets.length; i++) {
      const outlet = visitedOutlets[i];
      if (outlet.outletId === visitedOutlet.outletId) {
        found = true;
        outlet.visitDate = visitedOutlet.visitDate;
        break;
      }
    }

    if (!found) {
      visitedOutlets.push(visitedOutlet);
    }

    dispatch('update', { visitedOutlets }).catch(error => {
      if (error.code === 'ERR_VERSION_MISMATCH') {
        // attempt once more
        const visitedOutlets = state.user.visitedOutlets || [];
        let found = false;
        for (let i = 0; i < visitedOutlets.length; i++) {
          const outlet = visitedOutlets[i];
          if (outlet.outletId === visitedOutlet.outletId) {
            found = true;
            outlet.visitDate = visitedOutlet.visitDate;
            break;
          }
        }

        if (!found) {
          visitedOutlets.push(visitedOutlet);
        }
        dispatch('update', { visitedOutlets });
      }
    })
  },

  filterOutletByRecent({ commit, state, getters }) {
    if (!state.user) return;
    const params = {
      listingIds: getters.recentOutlets || []
    };

    businessApi.filterOutlet(params).then(resp => {
      if (resp && resp.listingvos) {
        commit('updateRecentOutletVOS', resp.listingvos)
      }
    });
  },

  updateLastSearchedAddr ({ commit }, address) {
    if (address) {
      commit('updateLastSearchedAddr', address)
    }
  },

  resetRecentOutletVOS ({ commit }) {
    commit('updateRecentOutletVOS', [])
  },

  updateCustomerInfo ({ dispatch }, user) {
    const updateCustomerInfo = (user) => {
      if (!user) {
        return null
      }

      const userCopy = Util.cloneObj(user);
      return {
        marketUserId: userCopy.userId,
        contactInfo: {
          name: {
            firstName: userCopy.firstName,
            lastName: userCopy.lastName
          },
          phoneNumber: userCopy.phoneNumber
        }
      };
    }
    dispatch('cart/updateCustomer', updateCustomerInfo(user), { root: true })
  },

  async filterOrders ({ commit }, params) {
    if (!state.user) {
      return null
    }
    let rParams = {
      marketUserId: state.user.userId
    };
    const result = await userApi.filterOrders(rParams);
    if (Util.isEmpty(result.orders)) {
      commit('updateRecentOrders', []);
      return;
    }
    commit('updateRecentOrders', result.orders);
  },

  updateCurrentLocation ({ commit }, address) {
    if (address) {
      commit('updateCurrentLocation', address)
    }
  }
};

const mutations = {
  initialize(state) {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const lastSearchedAddr = JSON.parse(localStorage.getItem('lastSearchedAddr'));
    if (currentUser) {
      state.user = currentUser;
    }
    if (lastSearchedAddr) {
      state.lastSearchedAddr = lastSearchedAddr;
    }

    state.sessionToken = localStorage.getItem('sessionToken');
  },

  enforceSignin(state, enforce) {
    state.enforceSignin = enforce;
  },

  updateVerifyToken(state, token) {
    state.verifyToken = token;
  },

  updateUser(state, user) {
    state.user = user;
    localStorage.removeItem('currentUser');
    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
    }
  },

  updateSessionToken(state, token) {
    state.sessionToken = token;
    localStorage.removeItem('sessionToken');
    if (token) {
      localStorage.setItem('sessionToken', token);
    }
  },

  updateRecentOutletVOS(state, listingVOS) {
    state.recentOutletVOs = listingVOS
  },

  updateLastSearchedAddr(state, address) {
    state.lastSearchedAddr = address;
    if (address) {
      localStorage.setItem('lastSearchedAddr', JSON.stringify(address));
    }
  },

  updateRecentOrders(state, orders) {
    state.recentOrders = orders
  },

  updateCurrentLocation(state, address) {
    state.currentLocation = address
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
